// The WordPress plugin in the customer area: download it, approve its connection and see (and
// unlink) the installations linked to the organisation's websites. The plugin only reads the results
// of the website it is installed on; complete scans and PDF reports stay in the platform.
import { CheckIcon, DownloadIcon, ExclamationTriangleIcon, LinkBreak2Icon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  authorizeWordPress,
  can,
  createMcpToken,
  type McpToken,
  type McpTokenCreated,
  mcpTokensQuery,
  meQuery,
  pluginInfoQuery,
  revokeMcpToken,
  unlinkWordPress,
  type WpConnection,
  wpConnectionsQuery,
  wpPreviewQuery,
} from '../api';
import { Button, buttonClass, ConfirmButton, Field, Message, Page } from '../ui';
import { formatBytes } from '../util';

/** Used out of the plan's limit, as a bar and as words. */
export function LinkedSitesMeter({
  used,
  limit,
  plan,
}: {
  used: number;
  limit: number | null;
  plan: string;
}) {
  const { t } = useTranslation();
  const text =
    limit === null
      ? t('wordpress.usage.unlimited', { used })
      : t('wordpress.usage.of', { used, limit, plan: t(`users.plans.${plan}`) });
  return (
    <div className="flex flex-col gap-2">
      <p className="font-bold">{text}</p>
      {limit !== null && (
        <progress
          className="wp-meter"
          max={limit}
          value={Math.min(used, limit)}
          aria-label={t('wordpress.usage.label')}
        >
          {used}/{limit}
        </progress>
      )}
    </div>
  );
}

function UnlinkButton({ connection, onDone }: { connection: WpConnection; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  return (
    <ConfirmButton
      label={t('wordpress.list.unlink')}
      hidden={connection.site.name}
      title={t('wordpress.list.unlinkTitle', { name: connection.site.name })}
      body={t('wordpress.list.unlinkBody')}
      confirm={t('wordpress.list.unlinkConfirm')}
      cancel={t('wordpress.list.cancel')}
      onConfirm={onDone}
    />
  );
}

/**
 * «Integraciones»: the platforms SiteNorma can be connected to, each one a section of its own. Today:
 * the WordPress plugin and MCP for IDE agents.
 */
export function IntegrationsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery);
  const plugin = useQuery(pluginInfoQuery);
  const connections = useQuery(wpConnectionsQuery);
  const mcpTokens = useQuery(mcpTokensQuery);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState(false);
  const [mcpNotice, setMcpNotice] = useState('');
  const [mcpError, setMcpError] = useState('');
  const [mcpName, setMcpName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<McpTokenCreated | null>(null);
  const [copied, setCopied] = useState<'token' | 'config' | 'link' | null>(null);
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
  // TanStack keeps the hash without «#».
  const section = location.hash === 'integracion-wordpress' ? 'wordpress' : 'mcp';

  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id) return;
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [location.hash]);

  async function unlink(connection: WpConnection) {
    setError(false);
    setNotice('');
    try {
      await unlinkWordPress(connection.id);
      await queryClient.invalidateQueries({ queryKey: ['wordpress'] });
      setNotice(t('wordpress.list.unlinked', { name: connection.site.name }));
    } catch {
      setError(true);
    }
  }

  async function createToken() {
    setMcpError('');
    setMcpNotice('');
    setCreating(true);
    try {
      const result = await createMcpToken(mcpName.trim() || undefined);
      setCreated(result);
      setMcpName('');
      await queryClient.invalidateQueries({ queryKey: ['mcp'] });
    } catch (err) {
      setMcpError(
        err instanceof ApiError && err.code === 'limite-tokens'
          ? t('mcp.errors.limit')
          : t('mcp.errors.generic'),
      );
    } finally {
      setCreating(false);
    }
  }

  async function revokeToken(token: McpToken) {
    setMcpError('');
    setMcpNotice('');
    try {
      await revokeMcpToken(token.id);
      await queryClient.invalidateQueries({ queryKey: ['mcp'] });
      setMcpNotice(t('mcp.list.revoked', { name: token.name }));
    } catch {
      setMcpError(t('mcp.errors.generic'));
    }
  }

  async function copyText(kind: 'token' | 'config' | 'link', value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
    } catch {
      setMcpError(t('mcp.errors.generic'));
    }
  }

  const canUnlink = (connection: WpConnection) =>
    can(me, 'integration', 'manage') || connection.authorizedById === me?.user.id;
  const canRevokeMcp = (token: McpToken) => can(me, 'integration', 'manage') || token.userId === me?.user.id;
  const atMcpLimit = mcpTokens.data !== undefined && mcpTokens.data.mine >= mcpTokens.data.maxPerUser;

  return (
    <Page title={t('integrations.title')} wide>
      <p className="max-w-3xl">{t('integrations.intro')}</p>

      <nav aria-label={t('integrations.nav')} className="site-tab-list">
        <button
          type="button"
          className="site-tab"
          aria-current={section === 'mcp' ? 'page' : undefined}
          onClick={() => void navigate({ to: '/integrations', hash: 'integracion-mcp' })}
        >
          {t('integrations.mcp')}
        </button>
        <button
          type="button"
          className="site-tab"
          aria-current={section === 'wordpress' ? 'page' : undefined}
          onClick={() => void navigate({ to: '/integrations', hash: 'integracion-wordpress' })}
        >
          {t('integrations.wordpress')}
        </button>
      </nav>

      <section aria-labelledby="integracion-mcp" className="integration">
        <h2 id="integracion-mcp" className="text-2xl font-bold">
          {t('mcp.title')}
        </h2>
        <p className="max-w-3xl">{t('mcp.intro')}</p>
        <p role="status" className="empty:hidden">
          {mcpNotice}
        </p>
        {mcpError && <Message tone="error">{mcpError}</Message>}

        {created ? (
          <div className="team-panel mt-4 flex flex-col gap-3" role="status">
            <h3 className="text-xl font-bold">{t('mcp.created.title')}</h3>
            <p>{t('mcp.created.body')}</p>
            <code className="block rounded-md bg-paper-alt px-3 py-2 font-mono break-all">
              {created.agentUrl}
            </code>
            <p className="flex flex-wrap gap-2">
              <Button type="button" variant="primary" onPress={() => void copyText('link', created.agentUrl)}>
                {copied === 'link' ? t('mcp.created.linkCopied') : t('mcp.created.copyLink')}
              </Button>
            </p>
            <h4 className="font-bold">{t('mcp.created.configTitle')}</h4>
            <p className="text-sm text-ink-muted">{t('mcp.created.configHint')}</p>
            <pre className="overflow-x-auto rounded-md bg-paper-alt px-3 py-2 text-sm">
              {JSON.stringify(created.config, null, 2)}
            </pre>
            <p className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onPress={() => void copyText('config', JSON.stringify(created.config, null, 2))}
              >
                {copied === 'config' ? t('mcp.created.configCopied') : t('mcp.created.copyConfig')}
              </Button>
              <Button type="button" variant="secondary" onPress={() => void copyText('token', created.token)}>
                {copied === 'token' ? t('mcp.created.copied') : t('mcp.created.copy')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onPress={() => {
                  setCreated(null);
                  setCopied(null);
                }}
              >
                {t('mcp.created.close')}
              </Button>
            </p>
          </div>
        ) : (
          <div className="wp-hero mt-4">
            <section aria-labelledby="mcp-crear" className="wp-download">
              <h3 id="mcp-crear" className="text-xl font-bold">
                {t('mcp.create.title')}
              </h3>
              {mcpTokens.data && (
                <p className="text-sm text-ink-muted">
                  {t('mcp.list.usage', { mine: mcpTokens.data.mine, max: mcpTokens.data.maxPerUser })}
                </p>
              )}
              {atMcpLimit ? (
                <p>{t('mcp.create.limit', { max: mcpTokens.data?.maxPerUser ?? 5 })}</p>
              ) : (
                <form
                  className="flex flex-col gap-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void createToken();
                  }}
                >
                  <Field
                    label={t('mcp.create.name')}
                    value={mcpName}
                    onChange={setMcpName}
                    placeholder={t('mcp.create.namePlaceholder')}
                    maxLength={60}
                    autoComplete="off"
                  />
                  <p>
                    <Button type="submit" variant="primary" isDisabled={creating}>
                      {creating ? t('mcp.create.working') : t('mcp.create.button')}
                    </Button>
                  </p>
                </form>
              )}
            </section>
            <section aria-labelledby="mcp-pasos" className="wp-steps">
              <h3 id="mcp-pasos" className="text-xl font-bold">
                {t('mcp.steps.title')}
              </h3>
              <ol className="verify-steps vertical">
                {(['one', 'two', 'three', 'four'] as const).map((step, index) => (
                  <li key={step}>
                    <span className="step-mark" aria-hidden="true">
                      {index + 1}
                    </span>
                    <span>{t(`mcp.steps.${step}`)}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}

        <section aria-labelledby="mcp-tokens" className="team-panel mt-6 flex flex-col gap-4">
          <p className="w-full text-sm text-ink-muted">{t('mcp.note')}</p>
          <h3 id="mcp-tokens" className="text-xl font-bold">
            {t('mcp.list.title')}
          </h3>
          {mcpTokens.isPending ? (
            <p>{t('app.loading')}</p>
          ) : mcpTokens.isError ? (
            <Message tone="error">{t('errors.generic')}</Message>
          ) : mcpTokens.data.tokens.length === 0 ? (
            <p>{t('mcp.list.empty')}</p>
          ) : (
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard.
            <section className="overflow-x-auto" aria-label={t('mcp.list.tableLabel')} tabIndex={0}>
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th scope="col">{t('mcp.list.name')}</th>
                    <th scope="col">{t('mcp.list.prefix')}</th>
                    <th scope="col">{t('mcp.list.owner')}</th>
                    <th scope="col">{t('mcp.list.created')}</th>
                    <th scope="col">{t('mcp.list.lastSeen')}</th>
                    <th scope="col">{t('mcp.list.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {mcpTokens.data.tokens.map((token) => (
                    <tr key={token.id}>
                      <td>{token.name}</td>
                      <td>
                        <code>{token.tokenPrefix}…</code>
                      </td>
                      <td>{token.userName}</td>
                      <td>{date(token.createdAt)}</td>
                      <td>{token.lastSeenAt ? date(token.lastSeenAt) : t('mcp.list.never')}</td>
                      <td>
                        {canRevokeMcp(token) && (
                          <ConfirmButton
                            label={t('mcp.list.revoke')}
                            hidden={token.name}
                            title={t('mcp.list.revokeTitle', { name: token.name })}
                            body={t('mcp.list.revokeBody')}
                            confirm={t('mcp.list.revokeConfirm')}
                            cancel={t('mcp.list.cancel')}
                            onConfirm={() => revokeToken(token)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </section>
      </section>

      <section aria-labelledby="integracion-wordpress" className="integration">
        <h2 id="integracion-wordpress" className="text-2xl font-bold">
          {t('wordpress.title')}
        </h2>
        <p className="max-w-3xl">{t('wordpress.intro')}</p>
        <p role="status" className="empty:hidden">
          {notice}
        </p>
        {error && <Message tone="error">{t('errors.generic')}</Message>}

        <div className="wp-hero">
          <section aria-labelledby="descarga" className="wp-download">
            <span className="eyebrow">{t('wordpress.download.eyebrow')}</span>
            <h3 id="descarga" className="text-xl font-bold">
              {t('wordpress.download.title')}
            </h3>
            {plugin.data ? (
              <>
                <p className="text-ink-muted">
                  {t('wordpress.download.meta', {
                    version: plugin.data.version,
                    size: formatBytes(plugin.data.size, i18n.language),
                    wp: plugin.data.requiresWp,
                    php: plugin.data.requiresPhp,
                  })}
                </p>
                <p>
                  <a href="/api/app/downloads/wordpress" download className={buttonClass('primary')}>
                    <DownloadIcon aria-hidden="true" />
                    {t('wordpress.download.button')}
                    <span className="sr-only"> ({plugin.data.filename})</span>
                  </a>
                </p>
                <details className="text-sm">
                  <summary className="cursor-pointer font-bold">{t('wordpress.download.checksum')}</summary>
                  <code className="mt-2 block rounded-md bg-paper-alt px-3 py-2 font-mono break-all">
                    {plugin.data.sha256}
                  </code>
                </details>
              </>
            ) : (
              <p>{plugin.isPending ? t('app.loading') : t('wordpress.download.unavailable')}</p>
            )}
          </section>

          <section aria-labelledby="pasos" className="wp-steps">
            <h3 id="pasos" className="text-xl font-bold">
              {t('wordpress.steps.title')}
            </h3>
            <ol className="verify-steps vertical">
              {(['one', 'two', 'three', 'four'] as const).map((step, index) => (
                <li key={step}>
                  <span className="step-mark" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span>{t(`wordpress.steps.${step}`)}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section aria-labelledby="vinculadas" className="team-panel mt-6 flex flex-col gap-4">
          <h3 id="vinculadas" className="text-xl font-bold">
            {t('wordpress.list.title')}
          </h3>
          {connections.isPending ? (
            <p>{t('app.loading')}</p>
          ) : connections.isError ? (
            <Message tone="error">{t('errors.generic')}</Message>
          ) : (
            <>
              <LinkedSitesMeter
                used={connections.data.used}
                limit={connections.data.limit}
                plan={connections.data.plan}
              />
              {connections.data.connections.length === 0 ? (
                <p>{t('wordpress.list.empty')}</p>
              ) : (
                // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling area must be reachable by keyboard.
                <section className="overflow-x-auto" aria-label={t('wordpress.list.tableLabel')} tabIndex={0}>
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b-2 border-line">
                        <th scope="col" className="py-2 pr-4">
                          {t('wordpress.list.site')}
                        </th>
                        <th scope="col" className="py-2 pr-4">
                          {t('wordpress.list.status')}
                        </th>
                        <th scope="col" className="py-2 pr-4">
                          {t('wordpress.list.versions')}
                        </th>
                        <th scope="col" className="py-2 pr-4">
                          {t('wordpress.list.lastSeen')}
                        </th>
                        <th scope="col" className="py-2 pr-4">
                          {t('wordpress.list.linkedBy')}
                        </th>
                        <th scope="col" className="py-2">
                          {t('wordpress.list.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.data.connections.map((connection) => (
                        <tr key={connection.id} className="border-b border-line align-middle">
                          <td className="py-2 pr-4">
                            <Link to="/sites/$siteId" params={{ siteId: connection.site.id }}>
                              {connection.site.name}
                            </Link>
                            <span className="block text-sm text-ink-muted wrap-anywhere">
                              {connection.site.url}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            {connection.site.verified ? (
                              <span className="status-pill ok">
                                <CheckIcon aria-hidden="true" /> {t('wordpress.list.verified')}
                              </span>
                            ) : (
                              <span className="status-pill unverified">
                                <ExclamationTriangleIcon aria-hidden="true" />{' '}
                                {t('wordpress.list.unverified')}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-sm">
                            {t('wordpress.list.versionsValue', {
                              wp: connection.wpVersion ?? '–',
                              plugin: connection.pluginVersion ?? '–',
                            })}
                          </td>
                          <td className="py-2 pr-4 text-sm">
                            {connection.lastSeenAt ? date(connection.lastSeenAt) : t('wordpress.list.never')}
                          </td>
                          <td className="py-2 pr-4">{connection.authorizedBy}</td>
                          <td className="py-2">
                            {canUnlink(connection) && (
                              <UnlinkButton connection={connection} onDone={() => unlink(connection)} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}
        </section>
      </section>
    </Page>
  );
}

/** The "WordPress" tab of a website: which installation is linked to it, if any. */
export function WordPressSection({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const connections = useQuery(wpConnectionsQuery);
  const linked = connections.data?.connections.find((connection) => connection.site.id === siteId);

  if (connections.isPending) return <p>{t('app.loading')}</p>;
  if (connections.isError) return <Message tone="error">{t('errors.generic')}</Message>;
  return (
    <section aria-labelledby="wp-sitio" className="flex flex-col gap-4">
      <h2 id="wp-sitio" className="text-2xl font-bold">
        {t('wordpress.tab.title')}
      </h2>
      {linked ? (
        <>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
            <dt className="font-bold">{t('wordpress.list.versions')}</dt>
            <dd>
              {t('wordpress.list.versionsValue', {
                wp: linked.wpVersion ?? '–',
                plugin: linked.pluginVersion ?? '–',
              })}
            </dd>
            <dt className="font-bold">{t('wordpress.list.lastSeen')}</dt>
            <dd>
              {linked.lastSeenAt
                ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
                    new Date(linked.lastSeenAt),
                  )
                : t('wordpress.list.never')}
            </dd>
            <dt className="font-bold">{t('wordpress.list.linkedBy')}</dt>
            <dd>{linked.authorizedBy}</dd>
          </dl>
          <p className="flex flex-wrap gap-x-6 gap-y-2">
            {/* Back to the plugin's own screen in the website's WordPress. */}
            <a
              href={`${linked.site.url.replace(/\/?$/, '/')}wp-admin/admin.php?page=sitenorma`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('wordpress.tab.open')}
              <span className="sr-only"> ({t('app.newTab')})</span>
            </a>
            <Link to="/integrations" hash="integracion-wordpress">
              {t('wordpress.tab.link')}
            </Link>
          </p>
        </>
      ) : (
        <>
          <p>{t('wordpress.tab.none')}</p>
          <p>
            <Link to="/integrations" hash="integracion-wordpress" className={buttonClass('secondary')}>
              <LinkBreak2Icon aria-hidden="true" />
              {t('wordpress.tab.link')}
            </Link>
          </p>
        </>
      )}
    </section>
  );
}

/** Where the plugin sends the browser: the member sees what is being linked and approves (or not). */
export function ConnectWordPressPage() {
  const { t } = useTranslation();
  const search = useSearch({ from: '/connect/wordpress' });
  const { data: me } = useQuery(meQuery);
  const complete = Boolean(search.site && search.cb && search.state && search.challenge);
  const preview = useQuery({ ...wpPreviewQuery(search.site ?? ''), enabled: complete });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function approve() {
    if (!complete) return;
    setBusy(true);
    setFailure(null);
    try {
      const { redirect } = await authorizeWordPress({
        site: search.site!,
        callback: search.cb!,
        state: search.state!,
        challenge: search.challenge!,
        name: search.name,
      });
      // The plugin finishes the link from its own admin: the code travels in this address.
      window.location.assign(redirect);
    } catch (e) {
      setBusy(false);
      const code = e instanceof ApiError ? e.code : null;
      setFailure(
        code &&
          ['limite-webs', 'permiso', 'callback', 'url', 'limite', 'vinculada', 'conflicto'].includes(code)
          ? code
          : 'generic',
      );
    }
  }

  if (!complete || (preview.isError && preview.error instanceof ApiError && preview.error.status === 400)) {
    return (
      <Page title={t('wordpress.connect.title')}>
        <Message tone="error">{t('wordpress.connect.invalid')}</Message>
        <p className="mt-6">
          <Link to="/">{t('wordpress.connect.home')}</Link>
        </p>
      </Page>
    );
  }
  if (preview.isPending) {
    return (
      <p role="status" className="mx-auto max-w-5xl px-4 py-10">
        {t('app.loading')}
      </p>
    );
  }
  if (preview.isError) {
    return (
      <Page title={t('wordpress.connect.title')}>
        <Message tone="error">{t('errors.generic')}</Message>
      </Page>
    );
  }
  const data = preview.data;
  const full = data.limit !== null && data.used >= data.limit && !data.known;
  return (
    <Page title={t('wordpress.connect.title')}>
      <p>{t('wordpress.connect.intro')}</p>
      <div className="wp-consent">
        <span className="eyebrow">{t('wordpress.connect.site')}</span>
        <strong className="wp-consent-host wrap-anywhere">{data.host}</strong>
        <span className="text-sm text-ink-muted wrap-anywhere">{data.url}</span>
        <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-[max-content_1fr]">
          <dt className="font-bold">{t('wordpress.connect.organization')}</dt>
          <dd>{me?.organization?.name}</dd>
          <dt className="font-bold">{t('wordpress.connect.account')}</dt>
          <dd>{me?.user.email}</dd>
        </dl>
        <LinkedSitesMeter used={data.used} limit={data.limit} plan={data.plan} />
      </div>

      <section aria-labelledby="alcance" className="mt-6">
        <h2 id="alcance" className="text-xl font-bold">
          {t('wordpress.connect.scopeTitle')}
        </h2>
        <ul className="wp-scope">
          <li>{t('wordpress.connect.can1')}</li>
          <li>{t('wordpress.connect.can2')}</li>
          <li>{t('wordpress.connect.can3')}</li>
          <li className="wp-scope-no">{t('wordpress.connect.cannot')}</li>
        </ul>
        <p className="text-sm text-ink-muted">{t('wordpress.connect.revoke')}</p>
      </section>

      {!data.canLink && <Message tone="error">{t('wordpress.connect.errors.permiso')}</Message>}
      {full && <Message tone="error">{t('wordpress.connect.errors.limite-webs')}</Message>}
      {failure && <Message tone="error">{t(`wordpress.connect.errors.${failure}`)}</Message>}

      <p className="mt-2 font-bold">{t('wordpress.connect.warning', { host: data.host })}</p>
      <div className="mt-4 flex flex-wrap items-center gap-6">
        <Button onPress={approve} isDisabled={busy || !data.canLink || full}>
          {busy ? t('wordpress.connect.working') : t('wordpress.connect.approve')}
        </Button>
        <Link to="/">{t('wordpress.connect.cancel')}</Link>
      </div>
    </Page>
  );
}
